import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import logoUrl from "@/assets/knowmeno-logo.svg";

const CREAM = "#FBF7F4";
const INK = "#231235";
const PLUM = "#2D0E43";

const NAV = [
  { label: "How It Works", to: "/#how" },
  { label: "Insights", to: "/#insights" },
  { label: "For Doctors", to: "/#doctors" },
  { label: "Research", to: "/medical-reset" },
];

const HIDDEN_PREFIXES = [
  "/app",
  "/sign-in",
  "/sign-up",
  "/forgot-password",
  "/reset-password",
  "/confirm-email",
  "/score",
  "/admin",
  "/dev",
  "/choose-plan",
];

export function SiteHeader() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  if (HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
    return null;
  }

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
          scrolled ? "km-glass km-header-scrolled" : "bg-transparent"
        }`}
        style={{ color: INK }}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:h-[88px] sm:px-8">
          <Link to="/" className="flex items-center" aria-label="KnowMeno home">
            <img
              src={logoUrl}
              alt="KnowMeno — Menopause Intelligence"
              height={56}
              style={{ height: 56, width: "auto", display: "block" }}
              className="sm:!h-[72px]"
            />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex">
            {NAV.map((item) => (
              <a
                key={item.to}
                href={item.to}
                className="km-nav-link text-[14px] font-medium"
                style={{ color: INK, letterSpacing: "0.01em" }}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/sign-in"
              className="km-cta-button hidden rounded-full px-[22px] py-3 text-[13px] font-medium tracking-tight sm:inline-flex"
              style={{ background: PLUM, color: CREAM }}
            >
              Sign In
            </Link>
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className="grid h-10 w-10 place-items-center rounded-full lg:hidden"
              style={{ color: INK }}
            >
              <Menu size={22} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>


      {/* Spacer so fixed header doesn't overlap content on non-home routes */}
      <div aria-hidden className="h-[72px] sm:h-[88px]" />

      {/* Mobile full-screen menu */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex flex-col"
          style={{ background: CREAM, color: INK }}
        >
          <div className="flex h-[72px] items-center justify-between px-5 sm:h-[88px] sm:px-8">
            <img src={logoUrl} alt="KnowMeno — Menopause Intelligence" style={{ height: 56, width: "auto" }} className="sm:!h-[72px]" />
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="grid h-10 w-10 place-items-center rounded-full"
            >
              <X size={22} strokeWidth={1.5} />
            </button>
          </div>
          <nav className="flex flex-1 flex-col items-start gap-6 px-7 pt-8">
            {NAV.map((item) => (
              <a
                key={item.to}
                href={item.to}
                onClick={() => setOpen(false)}
                style={{ fontFamily: "var(--font-serif)", fontSize: 38, lineHeight: 1.05, color: INK }}
              >
                {item.label}
              </a>
            ))}
            <Link
              to="/sign-in"
              onClick={() => setOpen(false)}
              className="mt-6 inline-flex rounded-full px-7 py-3.5 text-[14px] font-medium"
              style={{ background: PLUM, color: CREAM }}
            >
              Sign In
            </Link>
          </nav>
        </div>
      )}
    </>
  );
}
